'use client';
import './grid-placement.css';
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { GridBoard } from './GridBoard';
import { GridLot } from './GridLot';
import { GridShareButton } from './GridShareButton';
import { GridDistribution } from './GridDistribution';
import { KeepPlayingFooter } from '../../_components/KeepPlayingFooter';
import { DailySlateNav } from '../../_components/DailySlateNav';
import { useGridDrag } from './useGridDrag';
import { shareGrid, type ShareCell } from '../lib/select';
import {
  GRID_PAR,
  carFitsCell,
  scoreForMoves,
  type GridBoardResponse,
  type GridMode,
  type GridMove,
  type GridResultView,
  type TrapNote,
} from '../lib/types';

const useIsoLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

// Honest theme line for regular boards: named after the axis DIMENSIONS
// (mechanically true by construction), never after an editorial label that
// could over-promise. Specials override this with the filter banner.
const AXIS_NAMES: Record<string, string> = {
  era: 'Decades',
  origin: 'Nations',
  continent: 'Continents',
  drivetrain: 'Drivetrains',
  aspiration: 'Induction',
  body: 'Body styles',
  cylinders: 'Cylinders',
  gearbox: 'Gearboxes',
  stats: 'Vital stats',
  engine: 'Engines',
  'open-top': 'Open air',
};

function axesTheme(puzzle: { rows: { dim: string }[]; cols: { dim: string }[] }): string {
  const name = (dim: string) => AXIS_NAMES[dim] ?? dim;
  const colDims = Array.from(new Set(puzzle.cols.map((c) => c.dim)));
  return `${name(puzzle.rows[0].dim)} × ${colDims.map(name).join(' · ')}`;
}

function getSessionKey(): string {
  if (typeof window === 'undefined') return 'ssr';
  let k = window.localStorage.getItem('grid_session_key');
  if (!k) {
    k =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : String(Math.random()).slice(2);
    window.localStorage.setItem('grid_session_key', k);
  }
  return k;
}

/** Reduce a move log to the final board (client mirror of the worker replay —
 *  display only; the server re-derives the authoritative result). */
function boardOf(log: GridMove[]): (string | null)[] {
  const board = new Array<string | null>(9).fill(null);
  for (const m of log) {
    if (m.m === 'p') board[m.cell] = m.car;
    else board[m.cell] = null;
  }
  return board;
}

interface DoneState {
  mode: GridMode;
  moves: number;
  faults: number;
  score: number;
  solved: boolean;
  traps: TrapNote[];
}

export function GridGame({
  board,
  userId,
}: {
  board: GridBoardResponse;
  userId?: string;
}) {
  const { puzzle } = board;
  const carById = useMemo(
    () => new Map(puzzle.cars.map((c) => [c.id, c])),
    [puzzle.cars],
  );

  const [log, setLog] = useState<GridMove[]>([]);
  const [placements, setPlacements] = useState<(string | null)[]>(() =>
    new Array<string | null>(9).fill(null),
  );
  const [selected, setSelected] = useState<string | null>(null);
  const [armed, setArmed] = useState<number | null>(null);
  const [mode, setMode] = useState<GridMode>('easy');
  const [done, setDone] = useState<DoneState | null>(null);
  const [streak, setStreak] = useState<number | null>(null);
  const [confirmQuit, setConfirmQuit] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);

  const started = log.length > 0;
  const moves = useMemo(() => log.filter((m) => m.m === 'p').length, [log]);
  const faults = useMemo(
    () =>
      log.reduce((n, m) => {
        if (m.m !== 'p') return n;
        const car = carById.get(m.car);
        return n + (car && !carFitsCell(car, m.cell) ? 1 : 0);
      }, 0),
    [log, carById],
  );
  const placedIds = useMemo(
    () => new Set(placements.filter((c): c is string => c !== null)),
    [placements],
  );
  const filled = placedIds.size;

  function hydrate(result: GridResultView) {
    setPlacements(boardOf(result.movesLog ?? []));
    setLog(result.movesLog ?? []);
    setMode(result.mode);
    setDone({
      mode: result.mode,
      moves: result.moves,
      faults: result.faults,
      score: result.score,
      solved: result.solved,
      traps: result.traps ?? [],
    });
  }

  // Signed-in revisits come hydrated from the server; anonymous completions
  // live only in localStorage (same pattern as Duel/Connections).
  useIsoLayoutEffect(() => {
    if (board.result) {
      hydrate(board.result);
      return;
    }
    try {
      const raw = window.localStorage.getItem(`grid_result_${puzzle.id}`);
      if (!raw) return;
      const saved = JSON.parse(raw) as GridResultView;
      if (Array.isArray(saved.movesLog) && typeof saved.score === 'number') {
        hydrate(saved);
      }
    } catch {
      /* corrupt cache — play fresh */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board.result, puzzle.id]);

  function apply(nextLog: GridMove[], nextPlacements: (string | null)[]) {
    setLog(nextLog);
    setPlacements(nextPlacements);
    setSelected(null);
    setArmed(null);
    maybeFinish(nextLog, nextPlacements);
  }

  function doPlace(carId: string, cell: number) {
    if (done || placements[cell] !== null || placedIds.has(carId)) return;
    const nextLog = [...log, { m: 'p', car: carId, cell } as GridMove];
    const next = [...placements];
    next[cell] = carId;
    apply(nextLog, next);
  }

  function doRemove(cell: number) {
    if (done || placements[cell] === null) return;
    const nextLog = [...log, { m: 'r', cell } as GridMove];
    const next = [...placements];
    next[cell] = null;
    apply(nextLog, next);
  }

  function cellOk(carId: string | null, cell: number): boolean {
    if (!carId) return false;
    const car = carById.get(carId);
    return !!car && carFitsCell(car, cell);
  }

  function maybeFinish(nextLog: GridMove[], nextPlacements: (string | null)[]) {
    // One-shot mode never auto-finishes — the player commits explicitly via
    // the Submit button (see `submit`), so a full board just arms it.
    if (mode === 'hard') return;
    const nFilled = nextPlacements.filter((c) => c !== null).length;
    const nValid = nextPlacements.filter((c, i) => cellOk(c, i)).length;
    if (nFilled === 9 && nValid === 9) void finish(nextLog, nextPlacements);
  }

  async function finish(finalLog: GridMove[], finalPlacements: (string | null)[]) {
    const nMoves = finalLog.filter((m) => m.m === 'p').length;
    const nFilled = finalPlacements.filter((c) => c !== null).length;
    const nValid = finalPlacements.filter((c, i) => cellOk(c, i)).length;
    const won = nFilled === GRID_PAR && nValid === GRID_PAR;
    // Faults: easy mode counts every constraint-violating placement over the
    // whole game (each forces a re-place); one-shot mode judges only the final
    // submitted board, so it's wrong tiles at submit (nFilled − nValid).
    const nFaults =
      mode === 'hard'
        ? nFilled - nValid
        : finalLog.reduce((n, m) => {
            if (m.m !== 'p') return n;
            return n + (cellOk(m.car, m.cell) ? 0 : 1);
          }, 0);
    // Optimistic local result (mirror of the worker's replay: a win scores on
    // the move curve, anything else — one-shot boards, resignations — scores
    // its valid cells with solved=false); the POST response is authoritative
    // and also carries the trap reveal.
    const local: DoneState = {
      mode,
      moves: nMoves,
      faults: nFaults,
      score: won && mode === 'easy' ? scoreForMoves(nMoves) : nValid,
      solved: won,
      traps: [],
    };
    setDone(local);
    const save = (state: DoneState) => {
      try {
        window.localStorage.setItem(
          `grid_result_${puzzle.id}`,
          JSON.stringify({ ...state, movesLog: finalLog } satisfies GridResultView),
        );
      } catch {
        /* private mode / quota — non-fatal */
      }
    };
    save(local);
    try {
      const res = await fetch('/api/spot/grid/result', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(userId ? { 'X-User-Id': userId } : {}),
        },
        body: JSON.stringify({
          puzzleId: puzzle.id,
          sessionKey: getSessionKey(),
          mode,
          moves: finalLog,
        }),
      });
      if (res.ok) {
        const data = (await res.json()) as {
          moves?: number;
          faults?: number;
          score?: number;
          solved?: boolean;
          streak?: number;
          traps?: TrapNote[];
        };
        const server: DoneState = {
          mode,
          moves: data.moves ?? local.moves,
          faults: data.faults ?? local.faults,
          score: data.score ?? local.score,
          solved: data.solved ?? local.solved,
          traps: data.traps ?? [],
        };
        setDone(server);
        save(server);
        if (typeof data.streak === 'number' && data.streak > 0) setStreak(data.streak);
      }
    } catch {
      /* best-effort; the game already played out client-side */
    }
  }

  // ---- interactions (both tap orders + drag) ----

  function giveUp() {
    if (done || !started) return;
    if (!confirmQuit) {
      setConfirmQuit(true);
      setTimeout(() => setConfirmQuit(false), 3000);
      return;
    }
    setConfirmQuit(false);
    void finish(log, placements);
  }

  // One-shot commit: only once all nine slots are filled, and a two-tap confirm
  // because it's irreversible — this is the single moment the board is judged.
  function submit() {
    if (done || filled < GRID_PAR) return;
    if (!confirmSubmit) {
      setConfirmSubmit(true);
      setTimeout(() => setConfirmSubmit(false), 3000);
      return;
    }
    setConfirmSubmit(false);
    void finish(log, placements);
  }

  function tapCard(carId: string) {
    if (done || placedIds.has(carId)) return;
    if (armed !== null && placements[armed] === null) {
      doPlace(carId, armed);
      return;
    }
    setSelected((s) => (s === carId ? null : carId));
    setArmed(null);
  }

  function tapCell(cell: number) {
    if (done || placements[cell] !== null) return;
    if (selected) {
      doPlace(selected, cell);
      return;
    }
    setArmed((a) => (a === cell ? null : cell));
  }

  const { drag, dragProps } = useGridDrag({
    isCellOpen: (cell) => !done && placements[cell] === null,
    onTapCard: tapCard,
    onDrop: (carId, fromCell, target) => {
      if (done) return;
      if (target !== null) {
        if (fromCell !== null) doRemoveThenPlace(fromCell, carId, target);
        else doPlace(carId, target);
        return;
      }
      if (fromCell !== null) doRemove(fromCell);
    },
  });

  function doRemoveThenPlace(fromCell: number, carId: string, target: number) {
    if (done || placements[target] !== null || placements[fromCell] !== carId) return;
    const nextLog: GridMove[] = [
      ...log,
      { m: 'r', cell: fromCell },
      { m: 'p', car: carId, cell: target },
    ];
    const next = [...placements];
    next[fromCell] = null;
    next[target] = carId;
    apply(nextLog, next);
  }

  const dragCarFromLot = drag && drag.fromCell === null ? drag.carId : null;
  const dragFromCell = drag ? drag.fromCell : null;
  const dragCar = drag ? carById.get(drag.carId) : null;

  const hints = [
    'Anchor cars first — some only fit one slot.',
    'A ✗ tile costs a fault — the × takes it back.',
    'Three of the twelve fit nowhere. Trust the specs.',
  ];
  const hint =
    armed !== null
      ? 'Slot armed — now pick a car.'
      : mode === 'hard'
        ? 'No ✓/✗ until you submit — arrange all nine, then lock it in.'
        : hints[Math.min(2, Math.floor(faults / 2))];

  // Golf scorecard: per-cell strokes (placements this slot took over the whole
  // game). Easy mode colors holes par/bogey/worse; hard mode has one stroke
  // per hole by construction, so it colors by correctness instead.
  const strokes = useMemo(() => {
    const s = new Array<number>(9).fill(0);
    for (const m of log) if (m.m === 'p') s[m.cell]++;
    return s;
  }, [log]);
  const placedEver = useMemo(
    () => new Set(log.flatMap((m) => (m.m === 'p' ? [m.car] : []))),
    [log],
  );
  const shareCells = placements.map((c, i): ShareCell => {
    if (c === null) return 'empty';
    const valid = cellOk(c, i);
    if ((done?.mode ?? mode) === 'hard') return valid ? 'par' : 'bad';
    if (!valid || strokes[i] >= 3) return 'bad';
    return strokes[i] === 2 ? 'bogey' : 'par';
  });
  const share = done
    ? shareGrid(
        puzzle.number,
        done.mode,
        shareCells,
        done.solved,
        done.score,
        done.moves,
        done.faults,
        streak,
        board.date,
        puzzle.filters?.map((f) => f.label).join(' + ') ?? null,
      )
    : '';

  const title = !done
    ? ''
    : !done.solved
      ? done.mode === 'hard'
        ? `The grid beat you — ${done.score}/${GRID_PAR}.`
        : `Called it quits at ${done.score}/${GRID_PAR}.`
      : done.mode === 'hard'
        ? 'Locked in. Flawless.'
        : done.moves === GRID_PAR
          ? '⛳ Par. Clean sweep.'
          : done.faults === 0
            ? 'Zero faults, some rework.'
            : done.moves - GRID_PAR <= 3
              ? 'Solid parking job.'
              : 'Got there in the end.';

  return (
    <main className={`gp-stage${selected ? ' placing' : ''}`}>
      <header className="gp-head">
        <div className="gp-kicker">the motoring grid · saturdays</div>
        <h1 className="gp-title">
          The Motoring <em>Grid</em>
        </h1>
        {puzzle.filters?.length ? (
          <div className="gp-filter-banner special">
            Special board — every car in the lot is{' '}
            {puzzle.filters.map((f, i) => (
              <span key={f.id}>
                {i > 0 && ' and '}
                <b>{f.label}</b>
              </span>
            ))}
          </div>
        ) : (
          <div className="gp-filter-banner">{axesTheme(puzzle)}</div>
        )}
        <div className="gp-rules">
          <span>
            <b>Park 9 of the 12 cars</b> — each must fit its row <b>and</b> column.
          </span>
          <span>
            <b>Cars can fit more than one slot.</b> The puzzle is the
            arrangement that parks all nine.
          </span>
          <span>
            <b>3 are traps.</b> They fit nowhere — leave them on the lot.
          </span>
          {mode === 'hard' ? (
            <span>
              <b>One-shot:</b> rearrange all you like — no ✓/✗ hints. Fill all
              nine, then <b>Submit</b> to lock it in and score. No second try.
            </span>
          ) : (
            <span>
              Tap a car and a slot, or drag. ✗ = wrong slot · × removes it.
            </span>
          )}
        </div>
      </header>

      <div className="gp-status">
        <div>
          Placed <span className="gp-num">{filled}</span>/9
        </div>
        {/* Golf "moves/par" is an easy-mode concept — one-shot has unlimited
            drags. Live faults would leak the answer in one-shot, so it only
            appears once the board is scored. */}
        {mode !== 'hard' && (
          <div>
            Moves <span className="gp-num">{moves}</span>
            <span style={{ color: 'var(--gp-ink-4)' }}>/par {GRID_PAR}</span>
          </div>
        )}
        {(mode !== 'hard' || done) && (
          <div className="gp-faults">
            Faults <span className="gp-num">{done ? done.faults : faults}</span>
          </div>
        )}
        {!done && (
          <div className="gp-mode" title={started ? 'Locked once you start' : undefined}>
            <button
              type="button"
              className={mode === 'easy' ? 'on' : ''}
              disabled={started}
              onClick={() => setMode('easy')}
            >
              Classic
            </button>
            <button
              type="button"
              className={mode === 'hard' ? 'on' : ''}
              disabled={started}
              onClick={() => setMode('hard')}
            >
              One-Shot 🔒
            </button>
          </div>
        )}
        {done && done.mode === 'hard' && <div>🔒 one-shot</div>}
        <div className="gp-spacer" />
        {!done && <div className="gp-hint">{hint}</div>}
      </div>

      <div className="gp-arena">
        <GridBoard
          puzzle={puzzle}
          placements={placements}
          carById={carById}
          armedCell={armed}
          hoverCell={drag ? drag.hoverCell : null}
          liftCell={dragFromCell}
          hideFeedback={mode === 'hard' && !done}
          done={!!done}
          onCellTap={tapCell}
          onRemove={doRemove}
          cellDragProps={(carId, cell) => dragProps(carId, cell)}
        />
        <GridLot
          cars={puzzle.cars}
          placedIds={placedIds}
          selectedId={selected}
          liftId={dragCarFromLot}
          cardDragProps={(carId) => dragProps(carId, null)}
        />
      </div>

      {!done && mode === 'hard' && (
        <div className="gp-quit-row">
          <button
            type="button"
            className={`gp-submit${confirmSubmit ? ' arm' : ''}`}
            disabled={filled < GRID_PAR}
            onClick={submit}
          >
            {filled < GRID_PAR
              ? `Fill all nine to submit — ${filled}/9`
              : confirmSubmit
                ? 'Tap again — this locks it in'
                : 'Submit — lock it in & score'}
          </button>
        </div>
      )}
      {!done && mode !== 'hard' && started && (
        <div className="gp-quit-row">
          <button type="button" className={`gp-quit${confirmQuit ? ' arm' : ''}`} onClick={giveUp}>
            {confirmQuit ? 'Tap again to confirm' : 'Finish now — score it as it sits'}
          </button>
        </div>
      )}

      {drag && dragCar && (
        <div className="gp-dragclone" style={{ left: drag.x, top: drag.y }}>
          <div className="gp-nm">{dragCar.label}</div>
        </div>
      )}

      {done && (
        <section className={`gp-end${done.solved ? '' : ' lost'}`}>
          <div className="gp-k">
            {done.solved ? 'Grid complete' : 'Grid over'}
            {done.mode === 'hard' && <span className="gp-endmode">🎯 One-Shot</span>}
          </div>
          <h2>{title}</h2>
          {streak != null && streak >= 2 && (
            <p className="gp-streak">🔥 {streak}-day streak</p>
          )}
          <pre className="gp-share-pre">{share}</pre>
          <GridShareButton grid={share} />
          {done.traps.length > 0 && (
            <div className="gp-traps">
              <div className="gp-tt">The three traps, decoded</div>
              {done.traps.map((t) => {
                const bit = placedEver.has(t.id);
                return (
                  <p key={t.id}>
                    <span className={bit ? 'gp-trap-bit' : 'gp-trap-dodged'}>
                      {bit ? '✗' : '✓'}
                    </span>{' '}
                    <b>{t.label}</b> — {t.note}
                    {bit ? ' It got you.' : ' You never touched it.'}
                  </p>
                );
              })}
            </div>
          )}
          <GridDistribution date={board.date} yourScore={done.score} />
          <KeepPlayingFooter
            game="grid"
            date={board.date}
            userId={userId}
            archiveHref="/games?game=grid"
            includeSelf
          />
        </section>
      )}
      <DailySlateNav currentGame="grid" date={board.date} userId={userId} />
    </main>
  );
}
